import { useNavigation } from '@react-navigation/native'
import { PostTypes } from 'constants/postTypes'
import React, { useCallback, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { Avatar } from 'widgets/Avatar'
import { defaultAvatarImage } from 'widgets/Avatar/defaultAvatarImage'
import { Box } from 'widgets/Box'
import { FilledButton } from 'widgets/Button/FilledButton'
import { Icon } from 'widgets/Icon'
import { Image } from 'widgets/Image'
import { Pressable } from 'widgets/Pressable'
import { PressableText } from 'widgets/PressableText'
import { Text } from 'widgets/Text'

import { useCompletionRoom } from 'hooks/useCompletionRoom'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useFollow } from 'hooks/useFollow'
import { useGroup } from 'hooks/useGroup'
import { usePost } from 'hooks/usePost'
import { usePostRoom } from 'hooks/usePostRoom'
import { useSubscribed } from 'hooks/useSubscribed'
import { useUser } from 'hooks/useUser'

import { links } from 'utils/constants'
import { defaultPostThumbnailUrl } from 'utils/media'
import { getTextForNotificationType } from 'utils/notifications/getTextForNotificationType'
import commonStyles from 'utils/styles/commonStyles'
import { timeSince } from 'utils/time'

import { useCommentModal } from 'contexts/CommentModalContext'
import { useFocusMode } from 'contexts/FocusModeContext'

import { Notification, NotificationType } from 'types/notification'

type NotificationRowProps = {
  notification: Notification
}

export const NotificationRow = ({ notification }: NotificationRowProps) => {
  const navigation = useNavigation<any>()
  const { data: currentUser } = useCurrentUser()
  const { data: secondUser } = useUser({ userId: notification.secondUserId })
  const { data: secondGroup } = useGroup({ userId: notification.user?.id })
  const { data: secondGroupTrainer } = useUser({ userId: secondGroup?.trainer.id })

  const { data: postWithSteps } = usePost(notification.post?.id)

  const commentModalRef = useCommentModal()
  const postIdForRoom = notification.post?.id
  const { createRoom: createPostRoom } = usePostRoom({
    postId: postIdForRoom,
  })

  const completionIdForRoom = notification.completion?.id
  const { createRoom: createCompletionRoom } = useCompletionRoom({
    completionId: completionIdForRoom,
  })

  const { data: post } = usePost(notification.post?.id)
  const { data: group } = useGroup({ groupId: post?.program?.id })
  const { data: programTrainer } = useUser({ userId: post?.program?.trainer.id })

  const handleRowTap = () => {
    if (notification.type === 'accepted') {
      navigateToUpspace(notification.user?.username)
    } else {
      navigateToMemberships()
    }
  }

  const focusMode = useFocusMode()
  const { isSubscribed } = useSubscribed({ programId: group?.id })

  const navigateToUpspace = (username: string | undefined) => {
    if (isSubscribed) {
      focusMode.setGroup(group)
      return
    }
    navigation.navigate(links.GROUP_STACK, {
      screen: links.GROUP_PAGE,
      params: { username },
    })
  }

  const navigateToMemberships = () => {
    navigation.navigate(links.GROUP_STACK, {
      screen: links.MEMBERSHIPS_PAGE,
      params: { tab: 'pending' },
    })
  }

  const navigateToProfile = (username: string | undefined) => {
    navigation.navigate(links.PROFILE_STACK, {
      screen: links.MEMBER_PROFILE_PAGE,
      params: { username },
    })
  }

  const navigateToPost = (postId: string | undefined) => {
    navigation.navigate(links.POST_PAGE, {
      postId,
    })
  }

  const {
    follow,
    data: following,
    isLoading: isFollowLoading,
  } = useFollow({
    trainer: notification.user,
  })

  const { completion } = notification
  const isCompletionNotification = !!completion

  const isFollowing = !!following

  const avatar =
    notification.secondUserId && secondUser
      ? secondUser.avatar ?? defaultAvatarImage
      : notification.user?.avatar ?? defaultAvatarImage

  const username =
    notification.secondUserId && secondUser ? secondUser.username : notification.user?.username

  const { type } = notification

  let notificationType: NotificationType | 'commentOnCommentedPost'
  notificationType = type === 'follow' && isFollowing ? 'friend' : type
  notificationType = type === 'accepted' && !notification.secondUserId ? 'add' : notificationType

  const ownerId = isCompletionNotification ? completion?.user?.id : post?.creator?.id
  notificationType =
    type === 'comment' && ownerId && ownerId !== currentUser?.id
      ? 'commentOnCommentedPost'
      : notificationType

  const { isPrefix, text, prefixText } = getTextForNotificationType(notificationType)
  const programTitle = useMemo(
    () => group?.title ?? `${notification.user?.firstName} ${notification.user?.lastName}'s Group`,
    [notification.user?.firstName, notification.user?.lastName, group?.title],
  )
  const secondProgramTitle = useMemo(
    () => secondGroup?.title ?? `${secondUser?.firstName} ${secondUser?.lastName}'s Group`,
    [secondGroup?.title, secondUser?.firstName, secondUser?.lastName],
  )
  const timestamp = timeSince(notification.timestamp)

  const handleFollowBack = async () => {
    if (!follow.isLoading && !isFollowLoading && !following) {
      await follow.mutateAsync()
    }
  }

  const handleCommentClicked = async () => {
    await createCommentRoom(isCompletionNotification)
  }

  const createCommentRoom = useCallback(
    async (isCompletion: boolean) => {
      if ((!isCompletion && !postIdForRoom) || (isCompletion && !completionIdForRoom)) return
      const createRoom = isCompletion ? createCompletionRoom : createPostRoom
      const { id: roomId } = await createRoom.mutateAsync()

      let routeOptions: any = {
        roomId,
      }
      if (!isCompletion && postIdForRoom) {
        routeOptions = {
          ...routeOptions,
          postId: postIdForRoom,
        }
      }
      if (isCompletion && completionIdForRoom) {
        routeOptions = {
          ...routeOptions,
          completionId: completionIdForRoom,
        }
      }

      commentModalRef?.current?.present({ routeOptions })
    },
    [postIdForRoom, completionIdForRoom, createCompletionRoom, createPostRoom, commentModalRef],
  )

  const renderRowActions = () => {
    let content = null

    switch (notificationType) {
      case 'like':
      case 'highFive':
      case 'completion': {
        let computedImageUrl = ''
        if (post?.thumbnail) {
          computedImageUrl = post?.thumbnail.url
        } else if (postWithSteps && postWithSteps.steps?.length > 0) {
          computedImageUrl = postWithSteps.steps[0].file?.url ?? ''
        }
        content = (
          <Pressable onPress={() => navigateToPost(post?.id)}>
            <Box borderRadius='xs' width={40} height={48} style={commonStyles.overflowHidden}>
              <Image
                resizeMode='cover'
                source={{ uri: computedImageUrl }}
                style={commonStyles.fullSize}
              />
            </Box>
          </Pressable>
        )
        break
      }
      case 'add':
      case 'accepted': {
        content = <Icon color='black' name='chevron-right' />
        break
      }
      case 'comment':
      case 'commentOnCommentedPost': {
        content = (
          <Pressable onPress={handleCommentClicked}>
            <Icon color='v2primary' name='message-circle' />
          </Pressable>
        )
        break
      }
      case 'friend': {
        content = (
          <Text color='black' fontSize='sm' bold>
            Friends
          </Text>
        )
        break
      }
      case 'follow': {
        content = (
          <FilledButton title='Follow Back' height={28} fontSize='sm' onPress={handleFollowBack} />
        )
        break
      }
      default: {
        return null
      }
    }

    return (
      <Box flexDirection='row' margin={{ vertical: 'sm' }}>
        {content}
      </Box>
    )
  }

  if (notificationType === 'completion' && post?.type === PostTypes.ACTIVITY) {
    return null
  }

  let content = (
    <Box height={60} flexDirection='row' align='center' margin={{ vertical: 'xs' }}>
      <Box>
        {notificationType === 'add' ? (
          <Pressable onPress={() => navigateToUpspace(secondGroupTrainer?.username)}>
            <Box width={44} height={44} borderRadius='sm' style={commonStyles.overflowHidden}>
              <Image
                cacheKey={
                  secondGroup
                    ? // eslint-disable-next-line no-underscore-dangle
                      `${secondGroup?.id}-group-cover-${secondGroup?.cover?._original?.etag}-small`
                    : uuidv4()
                }
                imageWidth={160}
                resizeMode='cover'
                source={{ uri: secondGroup?.cover?.url ?? defaultPostThumbnailUrl }}
                style={commonStyles.fullSize}
              />
            </Box>
          </Pressable>
        ) : (
          <Pressable onPress={() => navigateToProfile(username)}>
            <Avatar height={44} width={44} image={avatar} />
          </Pressable>
        )}
      </Box>

      <Box flex={1} margin={{ horizontal: 'smd' }}>
        <Text color='greyDarkest' numberOfLines={2} ellipsizeMode='tail'>
          {prefixText && <Text color='black'>{prefixText}</Text>}
          {isPrefix && `${text} `}
          <PressableText
            bold
            color='black'
            onPress={() => {
              if (notificationType === 'add') {
                const trainerUsername = group?.title ? programTrainer?.username : username
                navigateToUpspace(trainerUsername)
              } else {
                navigateToProfile(username)
              }
            }}
          >
            {notificationType === 'add' ? `${programTitle}` : username}
          </PressableText>
          {!isPrefix && ` ${text}`}
          {notificationType === 'request' && (
            <PressableText
              bold
              color='black'
              onPress={() => navigateToUpspace(group?.title ? programTrainer?.username : username)}
            >
              {` ${programTitle}`}
            </PressableText>
          )}
          {notificationType === 'accepted' && (
            <PressableText color='black' bold onPress={() => navigateToUpspace(username)}>
              {` ${secondProgramTitle}`}
            </PressableText>
          )}
          <Text fontSize='sm' numberOfLines={1} color='greyDarker'>{`  ${timestamp}`}</Text>
        </Text>
      </Box>

      <Box flexDirection='row'>{renderRowActions()}</Box>
    </Box>
  )

  if (notification.type === 'accepted') {
    content = (
      <Pressable testID={`NotificationEvent-${notification.eventId}`} onPress={handleRowTap}>
        {content}
      </Pressable>
    )
  }

  return content
}
